/**
 * Rig v2 — ADDITIVE callable wrappers that live OUTSIDE the guardrail-locked
 * src/lib/ingestCallables.js (which must stay byte-identical with the live rig
 * flow). Anything Rig v2 needs that the live app doesn't goes here.
 *
 * registerWalkup creates a single walk-up / on-the-spot rosterEntry server-side;
 * the caller then arms it via the EXISTING ingest.arm({rosterEntryId}) — so the
 * one live arm path is reused unchanged (design brief §1, option A).
 *
 * sendGuestDelivery is the Captures tab's "Send to guest" (guest delivery).
 * reviewDelivery, getReviewPreview and confirmGuardianRecipient are the
 * Review tab's (admin only): Ivin's approval of each model before any email,
 * and the guardian check for guests under 18. previewNeverEmailedDeletion and
 * holdNeverEmailedCapture are the Never emailed tab's (admin only).
 *
 * fixRosterEmail and resendRegistrationPass are the station's replacement for
 * the public pass page's fix-email form (removed: a photographed pass could
 * redirect someone's delivery email). Both are crew-only callables.
 *
 * getProcessingQueue, setProcessingPaused, setProcessingJobAllowed and the
 * four task actions are the Processing tab's (ProcessingPanel.jsx, admin
 * only): the processing queue's staff controls.
 */
import { callable } from "../../../../firebase.js";

const call = async (name, payload, options) => {
  const fn = callable(name, options);
  const result = await fn(payload);
  return result.data;
};

// The two email actions run while a person stands at the rig: fail fast on
// venue Wi-Fi instead of holding the row for the SDK's 70 s default.
const EMAIL_ACTION_OPTIONS = { timeout: 15000 };

// Group check-in runs while the group stands at the rig, too. The screen reads
// the group from the live captureState, so an add that lands after a timeout
// still shows up there.
const GROUP_ACTION_OPTIONS = { timeout: 20000 };

// The upload status bar polls every few seconds: a poll that hangs must not
// pile up behind the next one. Pause and Discard get a little longer.
const UPLOAD_POLL_OPTIONS = { timeout: 8000 };
const UPLOAD_ACTION_OPTIONS = { timeout: 20000 };
// Download for processing: a few reads and two signatures, off the rig.
const DOWNLOAD_LINK_OPTIONS = { timeout: 30000 };

export const rigv2 = {
  /**
   * Create ONE walk-up / register-on-the-spot rosterEntry. Blank name → the
   * server stores "Unnamed" (name-later). Returns { ok, rosterEntryId, displayId, name }.
   * Registration v2: Register mode also sends the consent record fields
   * (consentVersion, ageBand, guardianName, guardianConsent) so the server
   * writes the same personFields.consent shape as the phone form.
   * Omit undefined fields so the callable's .optional() Zod accepts them (the
   * client serializes undefined → null on the wire, same guard as ingestCallables).
   */
  registerWalkup: ({ jobId, name, kind, email, phone, scene, consent, consentVersion, ageBand, guardianName, guardianConsent, source }) =>
    call("registerWalkup", {
      jobId,
      ...(name !== undefined ? { name } : {}),
      ...(kind !== undefined ? { kind } : {}),
      ...(email !== undefined ? { email } : {}),
      ...(phone !== undefined ? { phone } : {}),
      ...(scene !== undefined ? { scene } : {}),
      ...(consent !== undefined ? { consent } : {}),
      ...(consentVersion !== undefined ? { consentVersion } : {}),
      ...(ageBand !== undefined ? { ageBand } : {}),
      ...(guardianName !== undefined ? { guardianName } : {}),
      ...(guardianConsent !== undefined ? { guardianConsent } : {}),
      ...(source !== undefined ? { source } : {}),
    }),

  /**
   * Delete a just-created, never-captured walk-up entry (+ its qrSecret).
   * Idempotent; refuses captured / non-walk-up entries server-side. The rig
   * console no longer calls it after a failed arm (2026-09-29): a delete can
   * race another arm of the same person and remove the registration of a
   * guest on the rig; the console keeps the entry and picks it instead.
   */
  discardWalkupEntry: ({ jobId, rosterEntryId }) =>
    call("discardWalkupEntry", { jobId, rosterEntryId }),

  /**
   * Quick guest (the Rigs job picker): this month's studio walk-ins job, made
   * by the server the first time it is needed. No arguments: the month is the
   * server's. Returns { ok, jobId, displayName, month, created, consentReady,
   * registrationOpen, frozen } (functions/src/services/studioWalkins.ts).
   */
  openStudioWalkins: () => call("openStudioWalkins", {}, { timeout: 20000 }),

  /**
   * Replace the email on a roster entry and send the pass to the new address.
   * Returns { ok: true, emailMasked, emailed, status }.
   */
  fixRosterEmail: ({ jobId, rosterEntryId, email }) =>
    call("fixRosterEmail", { jobId, rosterEntryId, email }, EMAIL_ACTION_OPTIONS),

  /**
   * Send the registration pass again to the address on file. Returns the email
   * status record { status: "pending" | "sent" | "failed", at, attempts, messageId? }.
   */
  resendRegistrationPass: ({ jobId, rosterEntryId }) =>
    call("resendRegistrationPass", { jobId, rosterEntryId }, EMAIL_ACTION_OPTIONS),

  /**
   * Group check-in: add one more person to the capture armed on leaseId,
   * EXACTLY ONE of qrToken (a scanned pass) or rosterEntryId (a roster pick).
   * retake: true confirms adding someone already captured (the server refuses
   * them otherwise, details.reason "already_captured").
   * Returns { ok, state, member: { rosterEntryId, name, displayId } }.
   */
  addGroupMember: ({ jobId, leaseId, deviceId, qrToken, rosterEntryId, retake }) =>
    call("addGroupMember", {
      jobId,
      leaseId,
      deviceId,
      ...(qrToken !== undefined ? { qrToken } : {}),
      ...(rosterEntryId !== undefined ? { rosterEntryId } : {}),
      ...(retake === true ? { retake: true } : {}),
    }, GROUP_ACTION_OPTIONS),

  /**
   * Group check-in: take a person (not the first) out of the capture armed on
   * leaseId. Returns { ok, state, removed }.
   */
  removeGroupMember: ({ jobId, leaseId, deviceId, rosterEntryId }) =>
    call("removeGroupMember", { jobId, leaseId, deviceId, rosterEntryId }, GROUP_ACTION_OPTIONS),

  /**
   * Group check-in: arm a whole group in one step, the lead first (a group's
   * retake, "Capture group again"). The server checks everyone and arms the
   * full group or nobody. Returns { ok, state, leaseId }.
   */
  armGroup: ({ jobId, sessionId, deviceId, clientLeaseId, rosterEntryIds }) =>
    call("armGroup", { jobId, sessionId: sessionId ?? null, deviceId, clientLeaseId, rosterEntryIds }, GROUP_ACTION_OPTIONS),

  /**
   * Capture upload (docs/ingest/14-capture-upload-contract.md): the capture
   * PC uploader's last report for this job. Returns { status, stale, now };
   * status is null when no uploader has reported on this job.
   */
  getUploaderStatus: ({ jobId }) => call("getUploaderStatus", { jobId }, UPLOAD_POLL_OPTIONS),

  /** Ask the uploader to pause (paused: true) or go on. Returns { pauseRequested }. */
  setUploaderPause: ({ jobId, paused }) => call("setUploaderPause", { jobId, paused: Boolean(paused) }, UPLOAD_ACTION_OPTIONS),

  /**
   * Take a capture out of the upload: not uploaded, its cloud copy deleted.
   * Returns { ok, already, cloudDeleted, retryNeeded }: retryNeeded is true
   * when closing the upload or a delete failed (the Suite retries by itself;
   * pressing Discard again retries at once).
   */
  discardCapture: ({ jobId, sn }) => call("discardCapture", { jobId, sn }, UPLOAD_ACTION_OPTIONS),

  /**
   * Guest delivery (Captures tab): email one member of a capture the private
   * link to their page, which holds every capture of them. Each email makes a
   * new link; every link to the member's current address keeps working, and
   * a send to a changed address stops the old address's links at once. After
   * an unconfirmed send, calling again for the same capture within 23 hours
   * to the same address (while the download it promises is still on)
   * repeats that same email and link. firstSendOnly (the bulk send) refuses
   * with already_sent if this capture was sent to this member meanwhile.
   * Returns { status: "sent" | "unconfirmed" | "failed", emailMasked,
   * downloadUntil, viewUntil } (never the link).
   */
  sendGuestDelivery: ({ jobId, sn, rosterEntryId, firstSendOnly }) =>
    call("sendGuestDelivery", {
      jobId, sn, rosterEntryId,
      ...(firstSendOnly ? { firstSendOnly: true } : {}),
    }, { timeout: 30000 }),

  /**
   * Ivin's review (admin only): one decision about one capture. decision:
   * "approve" | "photos_only" | "hold" | "reprocess" | "leave_out". runId is
   * the run he saw (the candidate, else the run guests see, else null), and
   * photosGeneration the photographs he saw (approve and photos only): a newer
   * model or other photographs meanwhile refuse it (run_changed,
   * photos_changed). Returns { sn, state, status, kind, activeRunId,
   * candidateRunId }. The timeout covers the job's auto-send, when it is on.
   */
  reviewDelivery: ({ jobId, sn, decision, runId, photosGeneration, note }) =>
    call("reviewDelivery", {
      jobId, sn, decision, runId: runId ?? null,
      ...(photosGeneration ? { photosGeneration } : {}),
      ...(note ? { note } : {}),
    }, { timeout: 130000 }),

  /**
   * The review grid's thumbnails and photographs (admin only), for up to 40
   * captures of the job: { previews: [{ sn, record, run, thumb, photos,
   * full? }], expiresAt }. full (one capture): the model, cameras and AR
   * models too. Every link is a signed 10-minute view link.
   */
  getReviewPreview: ({ jobId, sns, full }) =>
    call("getReviewPreview", { jobId, sns, ...(full ? { full: true } : {}) }, { timeout: 60000 }),

  /**
   * The guardian check (admin only): staff confirm that the address on a
   * registration marked under 18 belongs to the parent or guardian. address
   * is the one shown (it must still be the one on the entry); how: "ledger" |
   * "asked" | "other". Returns { confirmed: true, emailMasked }.
   */
  confirmGuardianRecipient: ({ jobId, rosterEntryId, address, how }) =>
    call("confirmGuardianRecipient", { jobId, rosterEntryId, address, how }, EMAIL_ACTION_OPTIONS),

  /**
   * A guest asked New York Capture (by email, say) to delete everything
   * (admin only; the Captures tab's "Delete this guest's delivery"): the same
   * as their own "Delete everything now". Their page, download code and
   * registration details go; a capture shared with others stays for them.
   * Returns { deleted: true, alreadyEnded, capturesDeleted, scrub: "done" |
   * "pending" } (pending: their name on capture records goes in the nightly
   * run). The server allows 120 seconds.
   */
  deleteGuestDelivery: ({ jobId, rosterEntryId }) =>
    call("deleteGuestDelivery", { jobId, rosterEntryId }, { timeout: 130000 }),

  /*
   * "Open guest page" (the Review and Captures tabs, admin only): a 10-minute
   * admin view link of its own to the page one guest sees. Never the guest's
   * link, never an email; the server records who opened which page and when.
   * Returns { url, expiresAt }.
   */
  getAdminGuestPageLink: ({ jobId, rosterEntryId }) =>
    call("getAdminGuestPageLink", { jobId, rosterEntryId }, { timeout: 20000 }),

  /*
   * The processing queue's staff controls (the Processing tab, admin only;
   * docs/ingest/17-processing-queue-contract.md section 8). Nothing here
   * names a guest: rows carry SNs and counts.
   */

  /**
   * One job's tasks (oldest first) and counts, the switches ({ paused,
   * groupRecipeReady, allowedJobs }) and the processing computers'
   * reservations ({ uid, activeTaskId, recentFaults, cooldownUntil }).
   * Returns { counts, rows, truncated, switches, machines }.
   */
  getProcessingQueue: ({ jobId }) => call("getProcessingQueue", { jobId }, { timeout: 30000 }),

  /** Pause (true) or resume (false) the whole queue. Returns { paused }. */
  setProcessingPaused: ({ paused }) => call("setProcessingPaused", { paused: Boolean(paused) }, { timeout: 20000 }),

  /**
   * "Process only these jobs": switch one job on (allowed: true) or off. On
   * is refused while the job's review is off or its auto-send on. Returns
   * { jobId, allowed, allowedJobs, moved, already? }; moved: its tasks moved
   * into the queue (on) or back to waiting (off). Up to 500 moves: the
   * longer timeout.
   */
  setProcessingJobAllowed: ({ jobId, allowed }) =>
    call("setProcessingJobAllowed", { jobId, allowed: Boolean(allowed) }, { timeout: 130000 }),

  /** A task that needs staff, failed or was cancelled back to the queue with 3 more attempts. Returns { state }. */
  retryProcessingTask: ({ taskId }) => call("retryProcessingTask", { taskId }, { timeout: 60000 }),

  /** Take a task out of the queue for good (a computer working on it stops within a minute). Returns { state }. */
  cancelProcessingTask: ({ taskId, note }) =>
    call("cancelProcessingTask", { taskId, ...(note ? { note } : {}) }, { timeout: 20000 }),

  /** Hold a task until it is released (a computer working on it stops within a minute). Returns { state }. */
  holdProcessingTask: ({ taskId, note }) =>
    call("holdProcessingTask", { taskId, ...(note ? { note } : {}) }, { timeout: 20000 }),

  /** A held task back to the queue (or to the switch that still holds it). Returns { state }. */
  releaseProcessingTask: ({ taskId }) => call("releaseProcessingTask", { taskId }, { timeout: 20000 }),

  /**
   * Never emailed (admin only, docs/ingest/16-never-captured-deletion.md
   * section 8): the never-emailed deletion's preview for this job, reads
   * only. { nyToday, listFrom, deleteFrom, listed, auto, blockers,
   * acknowledgements, counts, captures: [{ sn, stage, detail, deadline, hold,
   * fate, due, members: [{ rosterEntryId, displayId, emailMasked, status,
   * addressIssue, canEmail }] }], people, toDeletePeople, toDeleteCaptures,
   * willDelete }. Never a name: the screen takes names from the roster.
   */
  previewNeverEmailedDeletion: ({ jobId }) => call("previewNeverEmailedDeletion", { jobId }, { timeout: 60000 }),

  /**
   * Hold one capture nobody was emailed 30 more days (admin only), with a
   * reason (3 to 300 characters). Allowed once its deletion is at most 30
   * days away. Returns { sn, from, untilDay, times }.
   */
  holdNeverEmailedCapture: ({ jobId, sn, reason }) =>
    call("holdNeverEmailedCapture", { jobId, sn, reason }, UPLOAD_ACTION_OPTIONS),

  /**
   * The Originals tab (admin only; docs/ingest/19-capture-pc-originals.md):
   * every original on the capture PC and what it is (safe to delete, keep,
   * a person looks first; on the drive or gone), the folders tied to no one
   * with their dates, the uploader's last check and the recycle bin.
   */
  getOriginals: ({ jobId }) => call("getOriginals", { jobId }, { timeout: 60000 }),

  /**
   * The job's switch "originals may go once their photos are in the cloud"
   * (originalsMayGo), or acceptRootScanId: the uploader now reads another
   * folder or drive and that is right, naming the check (inventory.scanId)
   * the admin looked at (refused with scan_changed if a newer one came in),
   * or releasePlaceScanId: every original of this job is gone from the drive,
   * which another job's uploader may then report (the check looked at).
   * One change at a time. Returns { ok, originalsMayGo }.
   */
  setOriginalsSettings: ({ jobId, originalsMayGo, acceptRootScanId, releasePlaceScanId }) =>
    call("setOriginalsSettings", {
      jobId,
      ...(originalsMayGo !== undefined ? { originalsMayGo: Boolean(originalsMayGo) } : {}),
      ...(acceptRootScanId ? { acceptRootScanId: String(acceptRootScanId) } : {}),
      ...(releasePlaceScanId ? { releasePlaceScanId: String(releasePlaceScanId) } : {}),
    }, UPLOAD_ACTION_OPTIONS),

  /**
   * A decision about a folder tied to no one: action "tie" (with
   * rosterEntryId: it is that registration's capture; the registration gets
   * a deletion hold), "not_guest" (not a guest's capture: safe to delete) or
   * "undo" (decisionId: the decision looked at; refused with decision_replaced
   * if the folder was decided again meanwhile). reason: a few words (a ledger
   * line). Returns { ok, decision }.
   */
  decideOriginalsFolder: ({ jobId, folder, action, rosterEntryId, reason, seen, decisionId }) =>
    call("decideOriginalsFolder", {
      jobId, folder, action,
      ...(rosterEntryId ? { rosterEntryId } : {}),
      ...(reason ? { reason } : {}),
      ...(seen ? { seen } : {}),
      ...(decisionId ? { decisionId } : {}),
    }, UPLOAD_ACTION_OPTIONS),

  /**
   * Download for processing (admin only; docs/ingest/14-capture-upload-setup.md):
   * two links, good for 10 minutes, to this capture's verified photos ZIP and
   * its info file. The Suite records each one with who asked and the computer
   * named (computer: what the person says, e.g. "P6"). Returns { jobId, sn,
   * uploadId, computer, expiresAt, photos: { url, fileName, bytes, md5 },
   * info: { url, fileName, bytes, md5 } }. Refusals carry details.reason
   * (not_in_cloud, discarded, delivery_closed, other_job, changed).
   */
  getCaptureDownloadLinks: ({ jobId, sn, computer }) =>
    call("getCaptureDownloadLinks", { jobId, sn, computer }, DOWNLOAD_LINK_OPTIONS),

  /**
   * The Settings tab's Automation (admin only): the job's three switches as
   * the queue and guest delivery read them. Returns { jobId, name,
   * processing, queuePaused, processingNow, reviewOn, autoSendOn, problem,
   * history: [{ setting, from, to, byEmail, via, at }] }.
   */
  getJobAutomation: ({ jobId, choices }) => call("getJobAutomation", { jobId, ...(choices ? { choices: true } : {}) }, { timeout: 20000 }),

  /**
   * One switch (admin only): setting "processing" | "requireReview" |
   * "autoSend", value true or false. The review off needs
   * confirmReviewOff ("turn off review"); the review off with auto-send on
   * needs confirmSendWithoutReview ("send without review") too. Refusals
   * carry details.reason (processing_on, processing_running, review_off,
   * confirmation_needed). Auto-send may be on while processing is (the
   * review must stay on). Returns the switches after it, with
   * changed (and moved, for processing). Processing moves up to 500 tasks:
   * the longer timeout. Slice 4.3: "autoPreviews" (value; switching it on
   * also makes the previews of the job's verified captures: previews
   * {created, existing, refused, more}) and "previewRecipients" (emails, each
   * one of previewRecipientChoices; refusal not_client_contact).
   */
  setJobAutomation: ({ jobId, setting, value, kind, presetIds, emails, confirmReviewOff, confirmSendWithoutReview }) =>
    call("setJobAutomation", {
      jobId, setting,
      ...(setting === "presets" ? { presetIds } : setting === "kind" ? { kind }
        : setting === "previewRecipients" ? { emails: Array.isArray(emails) ? emails : [] } : { value: Boolean(value) }),
      ...(confirmReviewOff ? { confirmReviewOff } : {}),
      ...(confirmSendWithoutReview ? { confirmSendWithoutReview } : {}),
    }, { timeout: 130000 }),

  /**
   * Previews by hand (admin only, slice 4.3): one capture (sn) or every
   * verified capture of the job, whatever "automate previews" says (off:
   * they wait for approval). Returns { jobId, sn, autoPreviewsOn, visited,
   * created, existing, refused: {reason: count}, failed, more }.
   */
  makePreviews: ({ jobId, sn, after }) => call("makePreviews", { jobId, ...(sn ? { sn } : {}), ...(after ? { after } : {}) }, { timeout: 130000 }),
  /**
   * SafeShare 4.5, the Review tab's client versions (admin only):
   * { client, versions: [{ sn, name, versionId, kind, label, stage, checks,
   * files, active, replaces, newerActive, deletionPending, lookFirst,
   * previewable, fingerprint, ... }], more, look? }. With sn, look holds that
   * capture's preview files signed for 10 minutes.
   */
  listVersionReview: ({ jobId, sn, after, versionsAfter, lookVersionId }) =>
    call("listVersionReview", {
      jobId, ...(sn ? { sn } : {}), ...(after ? { after } : {}), ...(versionsAfter ? { versionsAfter } : {}), ...(lookVersionId ? { lookVersionId } : {}),
    }, { timeout: 60000 }),
  /**
   * One decision (approve, hold, reprocess, remove) about exactly the version
   * listed (seen: its fingerprint); refused version_changed when it changed.
   */
  reviewVersion: ({ jobId, sn, versionId, decision, seen, note }) =>
    call("reviewVersion", { jobId, sn, versionId, decision, seen, ...(note ? { note } : {}) }, { timeout: 30000 }),
  /**
   * Look at one version's files (admin only; models and previews, pending or
   * not): { sn, versionId, kind, label, approval, from, files: [{ name, bytes,
   * url|null }], missing, manifest?, expiresAt }. Links last 10 minutes; each
   * look is audited on the server.
   */
  lookVersion: ({ jobId, sn, versionId }) => call("lookVersion", { jobId, sn, versionId }, { timeout: 60000 }),
  // SafeShare 4.4: one Zelma preview link per recipient (admin only).
  sendPreviewLinks: ({ jobId, emails }) => call("sendPreviewLinks", { jobId, ...(emails ? { emails } : {}) }, { timeout: 185000 }),

  /**
   * The Monitor tab (admin only): one page of the job's captures in SN order
   * (limit up to 250, after: the previous page's nextAfter), and on the first
   * page the computers, the uploader and the switches. Returns { jobId, now,
   * captures, nextAfter, limit, header }. No guest address; names as initials.
   */
  getPipelineMonitor: ({ jobId, after, limit }) =>
    call("getPipelineMonitor", { jobId, ...(after ? { after } : {}), ...(limit ? { limit } : {}) }, { timeout: 60000 }),
};
